// ============================================
// FILE: src/components/AboutUsScreen.jsx
// ============================================

import { ArrowLeft, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';

const AboutUsScreen = ({ onBack = null, appSettings, setActiveTab, appTheme = 'blue', isDesktop = false }) => {
  const appName = appSettings?.appName        || 'DealStore';
  const contact = appSettings?.supportContact || '+91 88266-00232';
  const email   = appSettings?.supportEmail   || 'support@dealstore.app,info@algolive.com';
  const address = appSettings?.corportateAddress
    || 'ALGOLIVE (OPC) PRIVATE LIMITED, OFFICE 719, 7th floor, SVH, 83 Metrostreet, Sector 83, Gurugram, Haryana 122004';
  const version      = appSettings?.version      || '1.0.0';
  const fssaiLicense = appSettings?.fssaiLicense || '';
  const logoSrc = `${process.env.PUBLIC_URL || ''}/logo.png`;

  const goHome = () => setActiveTab && setActiveTab('home');
  const openSupportPage = () => {
    if (typeof onBack === 'function') {
      onBack();
    }
    if (typeof setActiveTab === 'function') {
      setActiveTab('help');
    }
  };

  const milestones = [
    { icon: '👥', stat: '10K+',    label: 'Customers'  },
    { icon: '🚚', stat: '1 Lakh+', label: 'Deliveries' },
    { icon: '⭐', stat: '4.8',     label: 'Rating'     },
    { icon: '🌾', stat: '20+',     label: 'Farms'      },
    { icon: '🥛', stat: '50+',     label: 'Products'   },
    { icon: '🏆', stat: '3+',      label: 'Years'      },
  ];

  const values = [
    { icon: '🌿', title: 'Farm Fresh',      desc: 'Sourced directly from trusted local farms.',    bg: 'bg-[#e8f7f3]'  },
    { icon: '⏰', title: 'On-Time',         desc: 'Morning, afternoon or evening — your choice.',  bg: 'bg-[#fff8fa]'   },
    { icon: '🛡️', title: 'Quality Assured', desc: 'Strict quality checks before every delivery.',  bg: 'bg-[#fff8fa]' },
    { icon: '❤️', title: 'Customer First',  desc: 'Your satisfaction is everything to us.',        bg: 'bg-red-50'    },
  ];

  // Header style for theme consistency with CartScreen

  return (
    <div className={`pb-20 ${isDesktop ? 'min-h-full' : 'min-h-screen'}`} style={{ background: 'var(--primary-bg)', color: 'var(--text-main)' }}>
      {/* ─── HEADER ─── */}
      {/* Same bar as Shopping Bag on the cart page, with the brand block moved
          onto white beneath it — its old text colour was white-on-gradient. */}
      <div className="fashion-scope bg-white">
        <div
          className={`flex items-center gap-3 px-4 py-3 ${isDesktop ? 'max-w-[1200px] mx-auto' : ''}`}
          style={{ borderBottom: '1px solid var(--fx-line)' }}
        >
          <button
            onClick={onBack || goHome}
            className="p-1 -ml-1"
            title="Go Back"
            aria-label="Go back"
          >
            <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
          </button>
          <span className="fx-label">About Us</span>
        </div>

        <div
          className={`flex items-center gap-3 px-4 py-5 ${isDesktop ? 'max-w-[1200px] mx-auto' : ''}`}
          style={{ borderBottom: '1px solid var(--fx-line)' }}
        >
          <div
            className="w-12 h-12 rounded flex items-center justify-center flex-shrink-0"
            style={{ background: '#f5f5f6' }}
          >
            <img
              src={logoSrc}
              alt={appName}
              className="w-8 h-8 rounded object-contain"
            />
          </div>
          <div className="min-w-0">
            <h1 className="app-brand-title truncate" style={{ color: 'var(--fx-ink)' }}>{appName}</h1>
            <p className="text-xs font-medium truncate" style={{ color: 'var(--fx-ink-2)' }}>
              Fresh picks &amp; daily savings
            </p>
          </div>
        </div>
      </div>

      {/* ─── CONTENT ─── */}
      <div className="p-4 space-y-4">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {milestones.map((m, i) => (
            <div key={i} className="bg-white rounded p-3 text-center shadow-sm">
              <span className="text-2xl">{m.icon}</span>
              <p className="text-base font-bold text-[#282c3f] mt-1">{m.stat}</p>
              <p className="text-xs text-[#94969f]">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Our Mission */}
        <div className="bg-white rounded shadow-sm p-4">
          <h3 className="font-semibold text-[#282c3f] mb-3 flex items-center gap-2">
            <span className="text-lg">🎯</span> Our Mission
          </h3>
          <p className="text-[#535766] text-sm leading-relaxed">
            At <span className="font-semibold text-[#282c3f]">{appName}</span>, we believe every family
            deserves access to fresh, pure, and nutritious dairy products — delivered right to their door
            without hassle. We connect local farms with urban households, cutting out the middleman so you
            get the freshest products at the best prices.
          </p>
        </div>

        {/* Our Story */}
        <div className="bg-white rounded shadow-sm p-4">
          <h3 className="font-semibold text-[#282c3f] mb-3 flex items-center gap-2">
            <span className="text-lg">📖</span> Our Story
          </h3>
          <p className="text-[#535766] text-sm leading-relaxed mb-2">
            <span className="font-semibold text-[#282c3f]">{appName}</span> was born from a simple idea —
            milk should be as fresh as it gets, delivered before you wake up. What started as a small
            neighbourhood service has grown into a platform trusted by thousands of families across the region.
          </p>
          <p className="text-[#535766] text-sm leading-relaxed">
            We partner with certified local dairy farms, maintain a cold-chain from farm to doorstep,
            and use technology to make the entire process seamless for you.
          </p>
        </div>

        {/* What We Offer */}
        <div className="bg-white rounded shadow-sm p-4">
          <h3 className="font-semibold text-[#282c3f] mb-3 flex items-center gap-2">
            <span className="text-lg">🛒</span> What We Offer
          </h3>
          {[
            { text: 'Fresh cow & buffalo milk',          emoji: '🥛' },
            { text: 'Curd, paneer, ghee & butter',       emoji: '🧈' },
            { text: 'Flexible daily subscriptions',      emoji: '🔔' },
            { text: 'Same-day and morning delivery',     emoji: '🚚' },
            { text: 'Easy pause, skip & cancel options', emoji: '⚡' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
              <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
              <span className="text-sm text-[#535766]">{item.emoji} {item.text}</span>
            </div>
          ))}
        </div>

        {/* What We Stand For */}
        <div className="bg-white rounded shadow-sm p-4">
          <h3 className="font-semibold text-[#282c3f] mb-3 flex items-center gap-2">
            <span className="text-lg">💡</span> What We Stand For
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {values.map((v, i) => (
              <div key={i} className={`${v.bg} rounded p-3 text-center`}>
                <span className="text-2xl">{v.icon}</span>
                <p className="font-semibold text-[#282c3f] text-sm mt-1">{v.title}</p>
                <p className="text-[#94969f] text-xs mt-0.5 leading-snug">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Licences & Certifications */}
        {fssaiLicense && (
          <div className="bg-white rounded shadow-sm p-4">
            <h3 className="font-semibold text-[#282c3f] mb-3 flex items-center gap-2">
              <span className="text-lg">📋</span> Licences &amp; Certifications
            </h3>
            <div className="flex items-center gap-3 p-3 bg-[#e8f7f3] rounded">
              <div className="w-9 h-9 bg-[#e8f7f3] rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-base">🏛️</span>
              </div>
              <div>
                <p className="text-xs text-[#94969f]">FSSAI License No.</p>
                <p className="text-sm font-semibold text-[#282c3f] tracking-wide">{fssaiLicense}</p>
              </div>
              <div className="ml-auto">               
                <img
                  src={`${process.env.PUBLIC_URL || ''}/FSSAI_license.png`}
                  alt="FSSAI Certified"
                  className="h-10 object-contain"
                />
                 <img
                  src={`${process.env.PUBLIC_URL || ''}/fssai_logo.jpg`}
                  alt="FSSAI Certified"
                  className="h-7 object-contain"
                />
              </div>
            </div>
          </div>
        )}

        {/* Contact */}
        <div className="bg-white rounded shadow-sm p-4">
          <h3 className="font-semibold text-[#282c3f] mb-3 flex items-center gap-2">
            <span className="text-lg">📞</span> Get in Touch
          </h3>
          <div className="space-y-3">
            {contact && (
              <button
                type="button"
                onClick={openSupportPage}
                className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded hover:bg-[#fff8fa] transition-colors"
              >
                <div className="w-9 h-9 bg-[#fff8fa] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone size={16} className="text-[#ff3f6c]" />
                </div>
                <div>
                  <p className="text-xs text-[#94969f] text-left">Call Us</p>
                  <p className="text-sm font-semibold text-[#282c3f]">{contact}</p>
                </div>
              </button>
            )}
            {email && (
              <button
                type="button"
                onClick={openSupportPage}
                className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded hover:bg-red-50 transition-colors"
              >
                <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-[#94969f] text-left">Email Us</p>
                  <p className="text-sm font-semibold text-[#282c3f] break-all">{email}</p>
                </div>
              </button>
            )}
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded">
              <div className="w-9 h-9 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <MapPin size={16} className="text-[#94969f]" />
              </div>
              <div>
                <p className="text-xs text-[#94969f]">Authorised By ALGOLIVE OPC PVT LTD </p>
                <p className="text-sm font-semibold text-[#282c3f] leading-snug mt-0.5">{address}</p>
              </div>
            </div> 
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-[#94969f] py-2">
          v{version} &nbsp;·&nbsp; © {new Date().getFullYear()} {appName}. All rights reserved.
        </p>

      </div>
    </div>
  );
};

export default AboutUsScreen;